import React, { useCallback, useState } from 'react';
import AdminLayout from './AdminLayout';
import { GlassCard } from '@/components/brand/GlassCard';
import { GlowButton } from '@/components/brand/GlowButton';
import { AdminFormField } from '@/components/forms/FormField';
import { adminCreatePaymentLink } from '@/lib/api';
import { toast } from 'sonner';
import { Copy, CheckCircle2 } from 'lucide-react';
import { formatGBP } from '@/lib/utils';

const INITIAL_FORM = { name: '', email: '', reference: '', amount: '', notes: '' };

// --- Sub-components ---------------------------------------------------------

const AmountInput = ({ value, onChange }) => (
    <div>
        <label className="meta-eyebrow">Amount (GBP)</label>
        <div className="mt-2 flex items-center gap-2 rounded-la-sm bg-white/[0.04] border border-white/10 px-3 py-2">
            <span className="text-la-text2">£</span>
            <input
                type="number" min="10" max="250000" step="0.01"
                value={value} onChange={onChange} required
                data-testid="admin-link-amount"
                className="w-full bg-transparent text-base text-la-text placeholder:text-la-text3 focus:outline-none"
                placeholder="0.00"
            />
        </div>
    </div>
);

const PaymentLinkForm = ({ form, onChange, onSubmit, submitting }) => (
    <GlassCard className="p-6">
        <h3 className="font-display text-xl text-la-text">Generate a custom payment link</h3>
        <p className="mt-2 text-sm text-la-text2">Create a Stripe checkout link for an agreed amount and share it with your client.</p>
        <form onSubmit={onSubmit} className="mt-5 space-y-3" data-testid="admin-payment-link-form">
            <div className="grid grid-cols-2 gap-3">
                <AdminFormField label="Client name" value={form.name} onChange={onChange('name')} required testId="admin-link-name" />
                <AdminFormField label="Email" type="email" value={form.email} onChange={onChange('email')} required testId="admin-link-email" />
                <AdminFormField label="Reference" value={form.reference} onChange={onChange('reference')} testId="admin-link-reference" />
                <AmountInput value={form.amount} onChange={onChange('amount')} />
            </div>
            <div>
                <label className="meta-eyebrow">Notes</label>
                <textarea value={form.notes} onChange={onChange('notes')} rows={3} className="mt-2 w-full rounded-la-sm bg-white/[0.04] border border-white/10 px-3 py-2 text-sm" />
            </div>
            <div className="flex justify-end">
                <GlowButton type="submit" disabled={submitting} data-testid="admin-create-link-button">
                    {submitting ? 'Creating…' : 'Create link'}
                </GlowButton>
            </div>
        </form>
    </GlassCard>
);

const PaymentLinkResult = ({ result, onCopy }) => (
    <GlassCard className="p-6">
        <h3 className="font-display text-xl text-la-text">Last generated link</h3>
        {result ? (
            <div className="mt-4 space-y-3 text-sm">
                <div className="meta-eyebrow">Amount</div>
                <div className="font-display text-2xl text-la-text">{formatGBP(result.amount)}</div>
                <div className="meta-eyebrow mt-3">Checkout URL</div>
                <div className="flex items-center gap-2">
                    <input readOnly value={result.url} className="w-full rounded-la-sm bg-white/[0.04] border border-white/10 px-3 py-2 text-xs text-la-text2" data-testid="admin-link-result-url" />
                    <button onClick={onCopy} className="inline-flex items-center gap-2 text-sm text-la-text2 hover:text-la-text" data-testid="admin-link-copy">
                        <Copy className="h-4 w-4" /> Copy
                    </button>
                </div>
                <div className="flex items-center gap-2 text-emerald-300 text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> Link is single-use; reuse safe.</div>
            </div>
        ) : (
            <p className="mt-3 text-sm text-la-text3">Submit the form to generate a Stripe checkout link.</p>
        )}
    </GlassCard>
);

// --- Main page --------------------------------------------------------------

export default function AdminPaymentLinksPage() {
    const [form, setForm] = useState(INITIAL_FORM);
    const [result, setResult] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const onChange = useCallback((k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value })), []);

    const submit = useCallback(async (e) => {
        e.preventDefault();
        const amount = parseFloat(form.amount);
        if (!form.name || !form.email || !amount || amount <= 0) {
            toast.error('Complete name, email and amount.');
            return;
        }
        try {
            setSubmitting(true);
            const r = await adminCreatePaymentLink({ ...form, amount });
            setResult(r);
            toast.success('Payment link created.');
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Could not create link.';
            toast.error(typeof msg === 'string' ? msg : 'Error');
        } finally {
            setSubmitting(false);
        }
    }, [form]);

    const copy = useCallback(async () => {
        if (result?.url) {
            await navigator.clipboard.writeText(result.url);
            toast.success('Link copied to clipboard.');
        }
    }, [result]);

    return (
        <AdminLayout title="Payment Links">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <PaymentLinkForm form={form} onChange={onChange} onSubmit={submit} submitting={submitting} />
                <PaymentLinkResult result={result} onCopy={copy} />
            </div>
        </AdminLayout>
    );
}
